/**
 * Local-only camera tile order for this viewer's stage.
 * Never broadcast — other participants keep their own layout.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";
import type { TrackReference } from "@livekit/components-react";

function applyOrder(
  tracks: TrackReference[],
  orderIds: string[],
): TrackReference[] {
  if (orderIds.length === 0 || tracks.length <= 1) return tracks;
  const byId = new Map(
    tracks.map((t) => [t.participant.identity, t] as const),
  );
  const out: TrackReference[] = [];
  for (const id of orderIds) {
    const track = byId.get(id);
    if (track) {
      out.push(track);
      byId.delete(id);
    }
  }
  for (const track of tracks) {
    if (byId.has(track.participant.identity)) {
      out.push(track);
    }
  }
  return out;
}

function mergeOrder(prev: string[], ids: string[]): string[] {
  if (ids.length === 0) return [];
  if (prev.length === 0) return ids;
  const present = new Set(ids);
  const kept = prev.filter((id) => present.has(id));
  const keptSet = new Set(kept);
  const added = ids.filter((id) => !keptSet.has(id));
  const next = [...kept, ...added];
  if (
    next.length === prev.length &&
    next.every((id, i) => id === prev[i])
  ) {
    return prev;
  }
  return next;
}

function readStored(key: string | null): string[] {
  if (!key || typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.map((x) => String(x || "").trim()).filter(Boolean);
  } catch {
    return [];
  }
}

function writeStored(key: string | null, orderIds: string[]) {
  if (!key || typeof window === "undefined") return;
  try {
    if (orderIds.length === 0) {
      window.sessionStorage.removeItem(key);
      return;
    }
    window.sessionStorage.setItem(key, JSON.stringify(orderIds));
  } catch {
    /* ignore quota / private mode */
  }
}

export function useLocalCameraTileOrder(
  tracks: TrackReference[],
  storageKey: string | null,
) {
  const [orderIds, setOrderIds] = useState<string[]>(() =>
    readStored(storageKey),
  );
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const storageKeyRef = useRef(storageKey);
  storageKeyRef.current = storageKey;

  useEffect(() => {
    const ids = tracks.map((t) => t.participant.identity);
    setOrderIds((prev) => mergeOrder(prev, ids));
  }, [tracks]);

  useEffect(() => {
    writeStored(storageKeyRef.current, orderIds);
  }, [orderIds]);

  const orderedTracks = useMemo(
    () => applyOrder(tracks, orderIds),
    [tracks, orderIds],
  );

  const canRearrange = orderedTracks.length > 1;

  const moveIdentity = useCallback((fromId: string, toId: string) => {
    if (!fromId || !toId || fromId === toId) return;
    setOrderIds((prev) => {
      const from = prev.indexOf(fromId);
      const to = prev.indexOf(toId);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }, []);

  const onDragStart = useCallback(
    (identity: string) => (e: DragEvent) => {
      if (!canRearrange) return;
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", identity);
      setDraggingId(identity);
    },
    [canRearrange],
  );

  const onDragOver = useCallback(
    (identity: string) => (e: DragEvent) => {
      if (!canRearrange || !draggingId || draggingId === identity) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setOverId(identity);
    },
    [canRearrange, draggingId],
  );

  const onDrop = useCallback(
    (identity: string) => (e: DragEvent) => {
      e.preventDefault();
      const fromId =
        e.dataTransfer.getData("text/plain") || draggingId || "";
      moveIdentity(fromId, identity);
      setDraggingId(null);
      setOverId(null);
    },
    [draggingId, moveIdentity],
  );

  const onDragEnd = useCallback(() => {
    setDraggingId(null);
    setOverId(null);
  }, []);

  const resetOrder = useCallback(() => {
    setOrderIds(tracks.map((t) => t.participant.identity));
  }, [tracks]);

  return {
    orderedTracks,
    canRearrange,
    draggingId,
    overId,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    resetOrder,
  };
}
